import {
  ProjectType,
  type Listing,
  type NewConstructionData,
} from '@smg-real-estate/hg-rets-schema';
import cloneDeep from 'lodash.clonedeep';

import { hasNewConstructionLightProduct } from '@/helpers/hasVisibilityLightAccess';
import type { AgencyProduct } from '@/types-agency';

interface ProjectBoostInsertionContext {
  listing?: Pick<Listing, 'characteristics' | 'newConstructionData'>;
}

/** NC Boost is stored on the listing itself, not on the toplisting service. */
export const isProjectBoostActive = (
  insertion: ProjectBoostInsertionContext | undefined,
): boolean =>
  insertion?.listing?.newConstructionData?.projectType === ProjectType.NewConstructionBoost;

/**
 * NC Boost is only offered for new-construction listings of customers who do not already
 * get the new construction label through a New Construction Light package.
 */
export const isProjectBoostEligible = (
  insertion: ProjectBoostInsertionContext | undefined,
  activeProducts: Array<AgencyProduct> | undefined,
): boolean =>
  Boolean(insertion?.listing?.characteristics?.isNewBuilding) &&
  !hasNewConstructionLightProduct(activeProducts);

/**
 * Returns new-construction data with the NC Boost project type set or removed, leaving any
 * other new-construction fields (project name, project URL) untouched.
 *
 * Callers must only disable when NC Boost is the active project type, otherwise a different
 * tier such as New Construction Light would be dropped.
 */
export const applyProjectBoostToNewConstructionData = (
  newConstructionData: NewConstructionData | undefined,
  enabled: boolean,
): NewConstructionData | undefined => {
  if (enabled) {
    return { ...newConstructionData, projectType: ProjectType.NewConstructionBoost };
  }

  if (!newConstructionData) {
    return undefined;
  }

  const remaining = { ...newConstructionData };
  delete remaining.projectType;

  return remaining;
};

/** Returns a copy of the listing with the NC Boost project type set or removed. */
export const applyProjectBoostToListing = (listing: Listing, enabled: boolean): Listing => {
  const updatedListing = cloneDeep(listing);
  updatedListing.newConstructionData = applyProjectBoostToNewConstructionData(
    updatedListing.newConstructionData,
    enabled,
  );

  return updatedListing;
};
